import type { Deliverable, Epic, ProductGoal, TreeNode } from '@mona-roadmap/domain';
import { ChevronDown, ChevronRight, ExternalLink, Filter, Layers, ListChecks, Plus, Target } from 'lucide-react';
import { memo, useEffect, useRef, useState, type KeyboardEvent } from 'react';
import { copyWithToast } from '../lib/clipboard';
import { statusColor } from '../lib/colors';
import { fieldKey } from '../lib/fieldState';
import { isTempId } from '../lib/optimistic';
import { todayIso } from '../lib/format';
import type { ColumnDef } from '../lib/columns';
import { DateField, PeopleField, SelectField } from './cells/Editors';
import { useRoadmapContext } from './RoadmapContext';
import { ProgressBar } from './ui/Visuals';

interface RowProps {
  node: TreeNode;
  hasChildren: boolean;
  expanded: boolean;
  /** Visually selected (last focused) row. */
  selected: boolean;
  /** The single row in the tab order (roving tabindex). */
  tabStop: boolean;
  flash: boolean;
  /** Every second deliverable row is tinted so a long list stays easy to read. */
  stripe: boolean;
  renaming: boolean;
  columns: ColumnDef[];
  onFocusRow: (key: string) => void;
  onToggle: (key: string) => void;
  onStartRename: (key: string) => void;
  onStopRename: () => void;
}

export const TreeRow = memo(function TreeRow({ node, hasChildren, expanded, selected, tabStop, flash, stripe, renaming, columns, onFocusRow, onToggle, onStartRename, onStopRename }: RowProps) {
  const pending = node.entityId ? isTempId(node.entityId) : false;
  return (
    <div
      role="row"
      className="tg-row"
      data-key={node.key}
      data-kind={node.kind}
      data-entity={node.entityId ?? undefined}
      data-flash={flash || undefined}
      data-stripe={stripe || undefined}
      data-pending={pending || undefined}
      aria-level={node.depth + 1}
      aria-expanded={hasChildren ? expanded : undefined}
      aria-selected={selected}
      aria-busy={pending || undefined}
      tabIndex={tabStop ? 0 : -1}
      onFocusCapture={() => onFocusRow(node.key)}
      onMouseDownCapture={() => onFocusRow(node.key)}
    >
      {columns.map((c) => (
        <div key={c.key} role="gridcell" className="tg-cell" data-col={c.key} style={c.key === 'name' ? { paddingLeft: 8 + node.depth * 32 } : undefined}>
          {c.key === 'name' ? (
            <NameCell node={node} hasChildren={hasChildren} expanded={expanded} renaming={renaming} onToggle={onToggle} onStartRename={onStartRename} onStopRename={onStopRename} pending={pending} />
          ) : (
            <DataCell column={c.key} node={node} pending={pending} />
          )}
        </div>
      ))}
    </div>
  );
});

// ---- name column ------------------------------------------------------------------------------------

function NameCell({
  node,
  hasChildren,
  expanded,
  renaming,
  pending,
  onToggle,
  onStartRename,
  onStopRename,
}: {
  node: TreeNode;
  hasChildren: boolean;
  expanded: boolean;
  renaming: boolean;
  pending: boolean;
  onToggle: (key: string) => void;
  onStartRename: (key: string) => void;
  onStopRename: () => void;
}) {
  const ctx = useRoadmapContext();
  const clickTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => () => void (clickTimer.current && clearTimeout(clickTimer.current)), []);
  const synthetic = node.kind === 'standalone' || node.kind === 'no-goal';

  const onNameClick = () => {
    if (synthetic) {
      onToggle(node.key);
      return;
    }
    // A single click opens the editor; a double click renames in place.
    if (clickTimer.current) clearTimeout(clickTimer.current);
    clickTimer.current = setTimeout(() => ctx.openNode(node), 200);
  };
  const onNameDoubleClick = () => {
    if (synthetic || pending) return;
    if (clickTimer.current) clearTimeout(clickTimer.current);
    onStartRename(node.key);
  };

  const commitName = (next: string) => {
    onStopRename();
    const name = next.trim();
    if (!node.entityId || name === node.name) return;
    if (node.kind === 'deliverable') void ctx.mutations.patchDeliverable(node.entityId, { name });
    else if (node.kind === 'goal') void ctx.mutations.patchGoal(node.entityId, { name });
    else if (node.kind === 'epic') void ctx.mutations.patchEpic(node.entityId, { name });
  };

  return (
    <>
      {hasChildren ? (
        <button type="button" tabIndex={-1} className="grid size-6 flex-none place-items-center rounded text-dim hover:bg-raised hover:text-ink" aria-label={`${expanded ? 'Collapse' : 'Expand'} ${node.name}`} onClick={() => onToggle(node.key)}>
          {expanded ? <ChevronDown className="size-[18px]" aria-hidden /> : <ChevronRight className="size-[18px]" aria-hidden />}
        </button>
      ) : (
        <span className="size-6 flex-none" aria-hidden />
      )}
      {node.kind === 'goal' ? <Target className="size-4 flex-none text-accent-ink" aria-hidden /> : null}
      {node.kind === 'epic' ? <Layers className="size-4 flex-none text-faint" aria-hidden /> : null}
      {renaming ? (
        <RenameInput initial={node.name} onCommit={commitName} onCancel={onStopRename} />
      ) : (
        <button type="button" tabIndex={-1} className="name-btn min-w-0 truncate rounded px-0.5 text-left hover:text-accent-ink" title={node.name} onClick={onNameClick} onDoubleClick={onNameDoubleClick}>
          {node.name}
        </button>
      )}
      {node.url ? (
        <a
          href={node.url}
          target="_blank"
          rel="noopener noreferrer"
          tabIndex={-1}
          className="flex-none text-faint hover:text-accent-ink"
          aria-label={`Open ${node.name} in monday`}
          title="Open in monday"
          onClick={(e) => e.stopPropagation()}
          data-testid="monday-link"
        >
          <ExternalLink className="size-4" aria-hidden />
        </a>
      ) : null}
      {node.kind === 'goal' && node.goal ? (
        <span className="flex-none">
          <DateField
            variant="label"
            field="Target date"
            value={node.goal.targetDate}
            stateKey={fieldKey('g', node.goal.id, 'targetDate')}
            readOnly={pending}
            overdue={!!node.goal.targetDate && node.goal.targetDate < todayIso() && !ctx.metadata.options.pgStatus.some((o) => o.isDone && o.label === node.goal?.status)}
            onChange={(v) => void ctx.mutations.patchGoal(node.goal!.id, { targetDate: v })}
          />
        </span>
      ) : null}
      {node.kind === 'deliverable' && node.deliverable && node.deliverable.subitemCount > 0 ? (
        <span className="flex flex-none items-center gap-0.5 text-[12.5px] font-normal text-faint" title={`${node.deliverable.subitemCount} task(s)`}>
          <ListChecks className="size-3" aria-hidden />
          {node.deliverable.subitemCount}
        </span>
      ) : null}
      {(node.kind === 'goal' || node.kind === 'epic') && node.entityId && !pending ? (
        <button
          type="button"
          tabIndex={-1}
          className={`ml-auto grid size-6 flex-none place-items-center rounded hover:bg-raised ${ctx.isQuickFiltered(node) ? 'text-accent-ink' : 'text-faint hover:text-accent-ink'}`}
          aria-label={ctx.isQuickFiltered(node) ? `Clear filter on ${node.name}` : `Filter to ${node.name}`}
          aria-pressed={ctx.isQuickFiltered(node)}
          title={ctx.isQuickFiltered(node) ? 'Showing only this one. Click (or press Q) to show everything again' : `Show only ${node.kind === 'goal' ? 'this product goal' : 'this epic'} (Q)`}
          data-testid="quick-filter"
          onClick={() => ctx.quickFilter(node)}
        >
          <Filter className="size-3.5" fill={ctx.isQuickFiltered(node) ? 'currentColor' : 'none'} aria-hidden />
        </button>
      ) : null}
      {node.kind !== 'deliverable' && !pending ? (
        <button
          type="button"
          tabIndex={-1}
          className={`${(node.kind === 'goal' || node.kind === 'epic') && node.entityId ? '' : 'ml-auto '}grid size-5 flex-none place-items-center rounded text-faint hover:bg-raised hover:text-accent-ink`}
          aria-label={`Add deliverable to ${node.name}`}
          title="Add deliverable here"
          onClick={() => ctx.addDeliverableTo(node)}
        >
          <Plus className="size-3.5" aria-hidden />
        </button>
      ) : null}
    </>
  );
}

function RenameInput({ initial, onCommit, onCancel }: { initial: string; onCommit: (v: string) => void; onCancel: () => void }) {
  const [value, setValue] = useState(initial);
  const done = useRef(false);
  const finish = (commit: boolean) => {
    if (done.current) return;
    done.current = true;
    if (commit) onCommit(value);
    else onCancel();
  };
  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    e.stopPropagation();
    if (e.key === 'Enter') finish(true);
    else if (e.key === 'Escape') finish(false);
  };
  return (
    <input
      autoFocus
      className="input !h-8 min-w-0 flex-1"
      aria-label="Name"
      value={value}
      maxLength={255}
      onChange={(e) => setValue(e.target.value)}
      onFocus={(e) => e.currentTarget.select()}
      onKeyDown={onKeyDown}
      onBlur={() => finish(true)}
    />
  );
}

// ---- other columns ----------------------------------------------------------------------------------------

const Dash = () => <span className="text-faint">-</span>;

/** monday's own item key (MON-017) for deliverables. Goals and epics have no key column and show nothing. */
function IdCell({ node }: { node: TreeNode }) {
  const key = node.kind === 'deliverable' ? node.deliverable?.key : null;
  if (!key) return null;
  return (
    <button
      type="button"
      tabIndex={-1}
      className="id-btn"
      title={`Click to copy ${key}`}
      aria-label={`Copy ${key}`}
      data-testid="item-id"
      onClick={() => void copyWithToast(key)}
    >
      {key}
    </button>
  );
}

function DataCell({ column, node, pending }: { column: Exclude<ColumnDef['key'], 'name'>; node: TreeNode; pending: boolean }) {
  if (column === 'progress') return <ProgressCell node={node} />;
  if (column === 'itemId') return <IdCell node={node} />;
  if (node.kind === 'deliverable' && node.deliverable) return <DeliverableCell column={column} d={node.deliverable} pending={pending} />;
  if (node.kind === 'goal' && node.goal) return <GoalCell column={column} g={node.goal} pending={pending} />;
  if (node.kind === 'epic' && node.epic) return <EpicCell column={column} e={node.epic} pending={pending} />;
  return null;
}

function ProgressCell({ node }: { node: TreeNode }) {
  const { filtersActive } = useRoadmapContext();
  if (node.kind === 'deliverable') return null; // Done/Total is for goals and epics only
  const { done, total } = node.progress;
  const full = node.fullProgress.total;
  const title = filtersActive ? `${done} of ${total} matching deliverables done (${full} in total)` : `${done} of ${total} deliverables done`;
  return (
    <span className="flex min-w-0 items-center gap-2" title={title} data-testid="progress">
      <ProgressBar done={done} total={total} />
      <span className="text-[13.5px] font-normal text-ink">
        {done}/{total}
        {filtersActive ? <span className="text-faint"> of {full}</span> : null}
      </span>
    </span>
  );
}

function DeliverableCell({ column, d, pending }: { column: Exclude<ColumnDef['key'], 'name' | 'progress'>; d: Deliverable; pending: boolean }) {
  const ctx = useRoadmapContext();
  const { metadata, mutations } = ctx;
  const key = (field: string) => fieldKey('d', d.id, field);
  const scopingDone = metadata.options.scopingStatus.some((o) => o.isDone && o.label === d.scopingStatus);
  switch (column) {
    case 'goal': {
      const names = d.productGoalIds.map((id) => ctx.goalsById.get(id)?.name).filter(Boolean) as string[];
      return names.length ? (
        <span className="truncate text-dim" title={names.join(', ')}>
          {names.join(', ')}
        </span>
      ) : (
        <span className="text-faint italic">No Product Goal</span>
      );
    }
    case 'epic':
    case 'itemId':
      return null;
    case 'productArea':
      return d.productArea ? (
        <span className="truncate text-dim" title="Inherited from Epic (read-only)">
          {d.productArea}
        </span>
      ) : (
        <span className="text-faint" title="Inherited from Epic (read-only)">
          -
        </span>
      );
    case 'type':
      return <SelectField field="Type" style="chip" value={d.type} options={metadata.options.type} stateKey={key('type')} onChange={() => undefined} readOnly />;
    case 'pmOwner':
      return <PeopleField field="PM Owner" people={d.pmOwners} all={metadata.people} stateKey={key('pmOwnerIds')} readOnly={pending} onCommit={(ids) => void mutations.patchDeliverable(d.id, { pmOwnerIds: ids })} />;
    case 'assignee':
      return <PeopleField field="Assignee" people={d.assignees} all={metadata.people} stateKey={key('assigneeIds')} readOnly={pending} onCommit={(ids) => void mutations.patchDeliverable(d.id, { assigneeIds: ids })} />;
    case 'sprint':
      return <SelectField field="Sprint" style="sprint" value={d.sprint} options={metadata.options.sprint} sprintColors={ctx.sprintColors} stateKey={key('sprint')} readOnly={pending} onChange={(v) => void mutations.patchDeliverable(d.id, { sprint: v })} clearLabel="No sprint" />;
    case 'scopingStatus':
      return <SelectField field="Scoping Status" style="dot" value={d.scopingStatus} options={metadata.options.scopingStatus} stateKey={key('scopingStatus')} readOnly={pending} onChange={(v) => void mutations.patchDeliverable(d.id, { scopingStatus: v })} clearLabel="No scoping status" />;
    case 'scopingDue':
      return <DateField field="Scoping Due Date" value={d.scopingDue} stateKey={key('scopingDue')} readOnly={pending} overdue={!!d.scopingDue && d.scopingDue < todayIso() && !scopingDone} onChange={(v) => void mutations.patchDeliverable(d.id, { scopingDue: v })} />;
    case 'status':
      return <SelectField field="Status" style="dot" colorOf={statusColor} value={d.status} options={metadata.options.status} stateKey={key('status')} readOnly={pending} onChange={(v) => void mutations.patchDeliverable(d.id, { status: v })} clearLabel="No status" />;
  }
}

function GoalCell({ column, g, pending }: { column: Exclude<ColumnDef['key'], 'name' | 'progress'>; g: ProductGoal; pending: boolean }) {
  const { metadata, mutations } = useRoadmapContext();
  const key = (field: string) => fieldKey('g', g.id, field);
  if (column === 'status') {
    return <SelectField field="Goal status" style="dot" colorOf={statusColor} value={g.status} options={metadata.options.pgStatus} stateKey={key('status')} readOnly={pending} onChange={(v) => void mutations.patchGoal(g.id, { status: v })} clearLabel="No status" />;
  }
  return null;
}

function EpicCell({ column, e, pending }: { column: Exclude<ColumnDef['key'], 'name' | 'progress'>; e: Epic; pending: boolean }) {
  const { metadata, mutations } = useRoadmapContext();
  const key = (field: string) => fieldKey('e', e.id, field);
  switch (column) {
    case 'productArea':
      return <SelectField field="Product Area" style="text" value={e.productArea} options={metadata.options.productArea} stateKey={key('productArea')} readOnly={pending} onChange={(v) => void mutations.patchEpic(e.id, { productArea: v })} clearLabel="No product area" />;
    case 'type':
      return <SelectField field="Type" style="chip" value={e.type} options={metadata.options.type} stateKey={key('type')} readOnly={pending} onChange={(v) => void mutations.patchEpic(e.id, { type: v })} clearLabel="No type" />;
    case 'pmOwner':
      return <PeopleField field="PM Owner" people={e.pmOwners} all={metadata.people} stateKey={key('pmOwnerIds')} readOnly={pending} onCommit={(ids) => void mutations.patchEpic(e.id, { pmOwnerIds: ids })} />;
    case 'assignee':
      return <PeopleField field="R&D owner" people={e.rdOwners} all={metadata.people} stateKey={key('rdOwnerIds')} readOnly={pending} onCommit={(ids) => void mutations.patchEpic(e.id, { rdOwnerIds: ids })} />;
    default:
      return <Dash />;
  }
}
